import Link from 'next/link'
import { useTranslation } from 'next-i18next'
import classNames from 'classnames'
import React from 'react'
import { roboto } from '@/utils/fonts'
import type { Menu } from '@/utils/hooks/useMenu'
import styles from './MenuDropdown.module.scss'

type Props = {
  item: Menu
}

/**
 * A top-level menu entry, with its SEO subpages as a dropdown when it has any.
 *
 * Opening is CSS-only (:hover / :focus-within) rather than click state: the links must be
 * in the server-rendered HTML to be crawlable, which is the whole point of putting them in
 * the menu. Hidden with display:none rather than conditional rendering for the same reason.
 * Touch is unaffected — mobile renders MobileHeader instead.
 */
const MenuDropdown = ({ item }: Props) => {
  const { t } = useTranslation('common')

  if (!item.children?.length) {
    return (
      <li>
        <Link href={item.url}>{item.name}</Link>
      </li>
    )
  }

  // A child with children of its own heads a section; one without is a plain link at the
  // foot of the panel. Titling both left "Personbil" and "FAQ" looking like headings for
  // a list that never came.
  const sections = item.children.filter((child) => child.children?.length)
  const looseLinks = item.children.filter((child) => !child.children?.length)

  return (
    <li className={styles.component}>
      <Link href={item.url}>{item.name}</Link>
      <span className={styles.caret} aria-hidden="true" />
      <div className={styles.dropdown}>
        {sections.map((section) => (
          <div key={section.url} className={styles.section}>
            <Link href={section.url} className={styles.sectionTitle}>
              {section.name}
            </Link>
            <ul className={roboto.className}>
              {section.children?.map((child) => (
                <li key={child.url}>
                  <Link href={child.url}>{child.name}</Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
        {!!looseLinks.length && (
          <div className={classNames(styles.looseLinks, roboto.className)}>
            {looseLinks.map((link) => (
              <Link key={link.url} href={link.url}>
                {link.name}
              </Link>
            ))}
          </div>
        )}
        <Link href={item.url} className={styles.viewAll}>
          {t('menu.viewAll')}
        </Link>
      </div>
    </li>
  )
}

export default MenuDropdown
